import api from './api';

export interface PromoCampaign {
  id: number;
  name: string;
  campaign_type: string;
  campaign_type_display: string;
  status: string;
  status_display: string;
  description: string;
  start_date: string;
  end_date: string | null;
  target_animal_type: string;
  target_formula: number | null;
  target_formula_name: string | null;
  budget: string | null;
  custom_field_definitions: CustomFieldDef[];
  created_by: string | null;
  created_at: string;
  updated_at: string;
  notes: string;
  enrolled_count: number;
  total_invested: string;
}

export interface CustomFieldDef {
  key: string;
  label: string;
  type: 'number' | 'date' | 'text' | 'boolean';
  required?: boolean;
}

export interface CampaignEnrollment {
  id: number;
  campaign_id: number;
  customer_id: number;
  customer_name: string;
  enrolled_at: string;
  enrolled_by: string | null;
  status: string;
  custom_fields: Record<string, any>;
  total_promo_lbs: string;
  total_promo_value: string;
  orders_count: number;
  notes: string;
}

export interface CampaignPerformance {
  campaign_id: number;
  enrolled_count: number;
  total_invested: string;
  total_revenue: string;
  roi_pct: string;
  budget: string | null;
  budget_used_pct: string | null;
}

export interface ProjectedVsActual {
  summary: {
    projected_total: string;
    actual_total: string;
    roi_pct: string;
    avg_capture_pct: string;
  };
  monthly: Array<{
    month: string;
    projected_lbs: string;
    actual_lbs: string;
  }>;
  enrollments: Array<{
    customer_name: string;
    chick_count: number;
    chick_start_date: string;
    monthly: Array<{
      month: string;
      projected_lbs: string;
      actual_lbs: string;
    }>;
  }>;
}

// Campaign CRUD
export const getCampaigns = async (statusFilter?: string): Promise<PromoCampaign[]> => {
  const params: Record<string, string> = {};
  if (statusFilter) params.status = statusFilter;
  const response = await api.get('/promo-campaigns/', { params });
  return response.data;
};

export const getCampaign = async (id: number): Promise<PromoCampaign> => {
  const response = await api.get(`/promo-campaigns/${id}/`);
  return response.data;
};

export const createCampaign = async (data: Partial<PromoCampaign>): Promise<PromoCampaign> => {
  const response = await api.post('/promo-campaigns/', data);
  return response.data;
};

export const updateCampaign = async (id: number, data: Partial<PromoCampaign>): Promise<PromoCampaign> => {
  const response = await api.put(`/promo-campaigns/${id}/`, data);
  return response.data;
};

export const deleteCampaign = async (id: number): Promise<void> => {
  await api.delete(`/promo-campaigns/${id}/`);
};

// Enrollment
export const enrollCustomer = async (
  campaignId: number,
  data: { customer_id: number; custom_fields?: Record<string, any>; notes?: string }
): Promise<any> => {
  const response = await api.post(`/promo-campaigns/${campaignId}/enroll/`, data);
  return response.data;
};

export const getEnrollments = async (campaignId: number): Promise<CampaignEnrollment[]> => {
  const response = await api.get(`/promo-campaigns/${campaignId}/enrollments/`);
  return response.data;
};

export const deleteEnrollment = async (campaignId: number, enrollmentId: number): Promise<void> => {
  await api.delete(`/promo-campaigns/${campaignId}/enrollments/${enrollmentId}/`);
};

// Performance
export const getCampaignPerformance = async (campaignId: number): Promise<CampaignPerformance> => {
  const response = await api.get(`/promo-campaigns/${campaignId}/performance/`);
  return response.data;
};

export const getProjectedVsActual = async (campaignId: number): Promise<ProjectedVsActual> => {
  const response = await api.get(`/promo-campaigns/${campaignId}/projected-vs-actual/`);
  return response.data;
};
